import axios from "axios";
import { set, isEmpty, get } from "lodash";
import { datadogRum } from "@datadog/browser-rum";
import { getUrlAuthToken, getIsOnpremise, getCookies } from ".";
import constants from "./constants";
import getEnv from "../utils/getEnv";
import servicesUtils from "./servicesUtils";

const TIMEOUT = 180000;
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

export const getBackendURL = () => {
  const isOnpremise = getIsOnpremise();
  if (typeof window === "undefined") {
    return getEnv("NEXT_PUBLIC_API_URL");
  }

  if (!isOnpremise) {
    return getEnv("NEXT_PUBLIC_API_URL");
  }

  const hostname = window?.location.hostname;
  return `http://${hostname}:${getEnv("NEXT_PUBLIC_ONPREMISE_BACKEND_PORT")}`;
};

const axiosConnect = axios.create({
  baseURL: getBackendURL(),
  timeout: TIMEOUT,
  withCredentials: true,
  // exposedHeaders: ["set-cookie"] // there is no such key called exposedHeaders in type AxiosRequestConfig
});

axiosConnect.interceptors.request.use(async function interceptorFunc(config) {
  if (
    config.method === "post" ||
    config.method === "patch" ||
    config.method === "put" ||
    config.method === "delete"
  ) {
    const csrfToken = (window as any)["x-csrftoken"];
    if (csrfToken) {
      set(config, ["headers", servicesUtils.X_CSRF], csrfToken);
    }
  }

  const qXRProjectURLs = ["/auth/", "/login/", "/logout/", "/csrf/"];
  if (config.url && qXRProjectURLs.indexOf(config.url) === -1) {
    set(config, ["url"], `${config.url}`);
  }

  const { baseURL, url, params } = config;
  const isUserInfoCall = url?.includes("/userinfo") || url?.includes("/profile");

  const urlAuthToken = getUrlAuthToken();
  if (urlAuthToken && !isUserInfoCall && params) {
    params["url_auth_token"] = urlAuthToken;
  }

  if (isEmpty(baseURL)) {
    /* eslint-disable prefer-promise-reject-errors */
    return Promise.reject({ noValidUrl: true });
  }
  return config;
});

axiosConnect.interceptors.response.use(
  (response) => {
    return Promise.resolve(response);
  },
  (err) => {
    const errorStatus = get(err, ["response", "status"]);
    const url = get(err, ["config", "url"], "");
    const config = get(err, "config", {});
    const isUserInfoCall = get(err, ["config", "url"], "").includes("/userinfo");
    const stopRedirectLogin = get(err, ["config", "params", "stopRedirectLogin"], false);
    const isUrlAuthToken = !!getUrlAuthToken();
    const { code, message } = err;
    // if ([400, 403, 404, 500, 502, 503, 504].indexOf(errorStatus) !== -1) {
    //   // log 4xx, 5xx errors, not logging 401
    //   SentryIntegration.logError(err)
    // }

    if (code === "ECONNABORTED" || message?.includes("timeout")) {
      const maxRetries = config.maxRetries || MAX_RETRIES;
      let retryCount = config.retryCount || 0;
      if (retryCount < maxRetries) {
        retryCount++;
        return new Promise((resolve) => {
          setTimeout(
            () => resolve(axiosConnect({ ...config, retryCount })),
            config.retryDelay || RETRY_DELAY
          );
        });
      }
    }

    if (
      errorStatus === 401 &&
      !stopRedirectLogin &&
      window.location.pathname !== "/landing-page" &&
      window.location.pathname !== "/notifications-widget" &&
      window.location.pathname !== "/auth/login" &&
      !url?.includes("/auth/session") &&
      !url?.includes("/profile") &&
      window.location.pathname !== "/unauthorized-access" &&
      !isUrlAuthToken
    ) {
      const redirectURL = window.location.pathname;
      if (!redirectURL.includes("/workspace")) {
        window?.localStorage?.setItem(
          constants.PERSISTED.redirectAfterLogin,
          redirectURL
        );
      }

      window.location.href = window.location.origin + "/landing-page";
    } else {
      /* eslint-disable no-console */
      console.error("Invalid request", err);
      if (
        getEnv("NEXT_PUBLIC_APP_ENV") === "production" &&
        window.location.pathname !== "/landing-page"
      ) {
        const error = new Error(`XHR Error - ${errorStatus}`, {
          cause: err,
        });
        datadogRum.addError(error, {
          scope: "xhr-error",
          url: url,
        });
      }
    }
    return Promise.reject(err);
  }
);

export default axiosConnect;
